import React, {useState} from "react";
import {img} from "@/utils/pluginImg.js";
import {FAQ_INLINE_COUNT} from "@/constants/faqs.js";
import Modal from "@/customComponents/uiComponents/modal/Modal.jsx";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {FindingDriver} from "@/customComponents/BookRideSteps/modalPages/findingDriver.jsx";
import {FlightDelayedContent} from "@/customComponents/BookRideSteps/Components/flightDelayedContent.jsx";
import TrackDriverButton from "@/customComponents/sharedComponents/TrackDriverButton.jsx";
import {PayAfterDropoffContent} from "@/customComponents/BookRideSteps/modalPages/payAfterDropoff.jsx";
import {CancellationModificationRefundContent} from "@/customComponents/BookRideSteps/Components/cancellationModificationRefund.jsx";

// Action key → the view it opens. `findingDriver` is absent on purpose: it renders the
// full FindingDriver page rather than a bare content block (see actionBody).
const FAQ_ACTION_VIEWS = {
    findingDriver: null,
    flightDelayed: FlightDelayedContent,
    payAfterDropoff: PayAfterDropoffContent,
    cancellationModificationRefund: CancellationModificationRefundContent,
};

// Header title per action view — the title belongs to the MODAL HEADER, not to the
// content block. Views absent here keep an empty header on purpose: CMR carries its
// own tabbed header, findingDriver renders a full page with its own heading, and
// payAfterDropoff opens with a mint hero whose pill names the modal.
const FAQ_ACTION_TITLES = {};

// Single source for the FAQ accordion list — used for the inline "Popular questions"
// list and for the "More" lists (booking-flow popup/step + edit-page modal). Manages
// its own open-index state. Must sit inside a `.hopn-ride-faq` (and `.hopn-ride-latest`)
// ancestor for the styles to apply.
//
// A FAQ flagged with an `action` opens a dedicated view instead of expanding inline,
// mirroring the plugin's selectedCarDetailsLatest FAQ list. See FAQ_ACTION_VIEWS below
// for the set — adding one is a single entry there plus the same key in constants/faqs.js.
//
// `findingDriverPopup` (booking-modal steps only): on desktop render those views as
// the step's nested `.upper-modal` overlay — matching every other popup in the
// step — while mobile (and every other context: RideConfirm, edit page) keeps the
// shared bottom-sheet Modal. `.upper-modal` is position:absolute and only belongs
// inside the booking-modal shell, so it stays gated behind this prop. (The name is
// historical: it governs BOTH action views, not just the guide.)
export function FaqAccordionList({items, findingDriverPopup = false}) {
    const [openIdx, setOpenIdx] = useState(null);
    // The open action view's key, per shell. One state per shell rather than per view, so
    // a second action can't end up stacked on top of the first.
    const [sheetView, setSheetView] = useState(null);   // bottom-sheet Modal (mobile / non-step)
    const [upperView, setUpperView] = useState(null);   // desktop `.upper-modal` popup

    const openAction = (action) => {
        if (findingDriverPopup && window.innerWidth >= 576) {
            setUpperView(action);
        } else {
            setSheetView(action);
        }
    };

    // The body for whichever action is showing — the two shells render the same thing.
    const actionBody = (view) => {
        const Content = FAQ_ACTION_VIEWS[view];
        // findingDriver has no Content entry: it renders the full guide page, which
        // brings its own layout.
        return Content ? <Content/> : <FindingDriver hideHeader/>;
    };

    return (
        <>
            <ul className="hopn-ride-faq-list">
                {items.map((item, i) => {
                    const isAction = item.action in FAQ_ACTION_VIEWS;
                    return (
                        <li key={i} className={!isAction && openIdx === i ? "open" : ""}>
                            <button type="button" onClick={() => {
                                if (isAction) { openAction(item.action); return; }
                                setOpenIdx(openIdx === i ? null : i);
                            }}>
                                <div className="left-section">
                                    <div className="icon"><img src={img(item.icon)} alt=""/></div>
                                    <span>{item.q}</span>
                                </div>
                                <img src={img(isAction ? "arrow-right.svg" : (openIdx === i ? "arrow-up.svg" : "arrow-down.svg"))} alt=""/>
                            </button>
                            {!isAction && openIdx === i && (
                                <div className="hopn-ride-faq-answer">
                                    <div dangerouslySetInnerHTML={{__html: item.a}}/>
                                    {/* The answer is an HTML string, so anything with
                                        behaviour has to be a real component alongside it.
                                        A FAQ opts in with `cta: 'trackDriver'`. */}
                                    {item.cta === 'trackDriver' && (
                                        <TrackDriverButton className="hopn-ride-faq-cta" loaderColor="#fff">
                                            Download App
                                        </TrackDriverButton>
                                    )}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>

            {/* Action view — shared bottom-sheet Modal (mobile + non-step contexts like
                RideConfirm / edit page). The Modal supplies its own left/right gutters
                (FindingDriver renders flush), so no flushBody. */}
            <Modal isOpen={!!sheetView} onClose={() => setSheetView(null)}>
                <ModalHeader title={FAQ_ACTION_TITLES[sheetView] || ''} onClose={() => setSheetView(null)}/>
                {actionBody(sheetView)}
            </Modal>

            {/* Action view — desktop nested `.upper-modal` popup (booking modal steps
                only). Mirrors the step's other popups; modalPages-spacing supplies the
                left/right gutters the bodies don't carry themselves. */}
            {upperView && (
                <div className="upper-modal" style={{display: 'flex'}}>
                    <div className="upper-modal-inner">
                        <ModalHeader
                            inline={true} title={FAQ_ACTION_TITLES[upperView] || ''} extraClasses="less-space" totalSteps={0}
                            cancelButton={true} cancelButtonType="icon" backStep={null} showBackButton={false}
                            setStep={() => {}} closeModal={() => setUpperView(null)}
                        />
                        <div className="upper-modal-content padding-bottom-30">
                            <div className="modalPages-spacing">
                                {actionBody(upperView)}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// "Popular questions" section: head + inline FAQ list + "More" button. The parent
// decides what "More" opens (desktop popup / mobile step / edit-page modal) via onMore.
// The parent supplies the `.hopn-ride-latest` scoping wrapper.
// `findingDriverPopup` forwards to the inline list (see FaqAccordionList): booking
// steps pass it so the guide opens as the desktop `.upper-modal` popup.
// `title` / `chip` override the default heading — the booking-received perks card
// names the section ("Exclusive Airport Pickup Perks") and adds a category chip
// under it; everywhere else keeps the plain "Popular questions".
// `hideHead` drops the whole head — the heading AND the "ask me" button — leaving just the
// list. The phone-booking confirmation page uses it: the questions sit there as plain
// reading, with no assistant to ask.
export default function PopularQuestions({faqs = [], onMore, findingDriverPopup = false, title = 'Popular questions', chip = '', hideHead = false}) {
    return (
        // Head hidden → the card is just the list, so "More" moves from under it to beside it:
        // a row, the list taking the width and the link sitting at the foot of the last
        // question. On a phone there is no width to spare, so it stacks back underneath.
        <div className={`hopn-ride-faq${hideHead ? ' flex items-end gap-4 max-md:flex-col max-md:items-stretch' : ''}`}>
            {!hideHead && <div className="hopn-ride-faq-head">
                <h4>{title}</h4>
                <div className="hopn-ride-askme-wrap">
                    <button type="button" className="hopn-ride-askme" disabled>
                        <div className="limo-footer-wave">
                            <span className="limo-footer-dot"></span>
                            <span className="limo-footer-dot"></span>
                            <span className="limo-footer-dot"></span>
                        </div>
                        ask me <img src={img("voice.svg")} style={{filter: "brightness(0) invert(1)"}} alt=""/>
                    </button>
                    <span className="hopn-ride-askme-tooltip">Coming soon</span>
                </div>
            </div>}
            {chip && <span className="hopn-ride-faq-chip">{chip}</span>}
            {hideHead
                ? <div className="md:basis-[78%] md:shrink-0 min-w-0"><FaqAccordionList items={faqs.slice(0, FAQ_INLINE_COUNT)} findingDriverPopup={findingDriverPopup}/></div>
                : <FaqAccordionList items={faqs.slice(0, FAQ_INLINE_COUNT)} findingDriverPopup={findingDriverPopup}/>}
            {faqs.length > FAQ_INLINE_COUNT && (
                <div className={`more-button${hideHead ? ' md:flex-1 md:text-center! pb-2.5' : ''}`}>
                    <button type="button" className="hopn-ride-faq-more" onClick={onMore}>
                        More <img src={img("arrow-right.svg")} alt=""/>
                    </button>
                </div>
            )}
        </div>
    );
}
